import { GridColDef, GridRowId, GridValidRowModel } from '@mui/x-data-grid-premium';
import { GridApiCommunity } from '@mui/x-data-grid/models/api/gridApiCommunity';
import React, { ReactElement, Children, JSXElementConstructor, isValidElement } from 'react';
import { TypeTable } from '../table.component';

export const formColumnsAndFooter = ({
  children,
  columnVisibilityModel,
  viewCells,
}: IformColumnsAndFooterArgs): IformColumnsAndFooterRes => {
  const columns: GridColDef[] = [];
  let footer: ReactElement | null = null;

  Children.forEach(children, (child) => {
    if (isColumn(child)) {
      let visibility = true;
      if (columnVisibilityModel) {
        const keys = Object.keys(columnVisibilityModel);
        let findEl: string | null = null;
        for (const key of keys) {
          const groupField = key.split('group_')?.[1];
          /* The group keyword combines multiple columns that contain text after "group_" in their field name.
          Example: group_test: false. All columns containing the word "test" in the field name will be hidden*/
          if (groupField && child.props.field.includes(groupField)) findEl = key;
          else if (key === child.props.field) findEl = key;
        }
        findEl && (visibility = columnVisibilityModel[findEl]);
      }
      if (visibility) {
        columns.push({
          ...child.props,
          ...(viewCells?.actions[child.props.field] && {
            cellClassName: viewCells.actions[child.props.field],
          }),
        });
      }
    }
    if (isFooter(child)) footer = child;
  });

  return { columns, footer };
};

const isColumn = (child: TChild) => {
  const isValid = isValidElement(child);
  return isValid && typeof child.type === 'function' && child.type.name === 'Column';
};

const isFooter = (child: TChild) => {
  const isValid = isValidElement(child);
  return isValid && typeof child.type === 'function' && child.type.name === 'Footer';
};

export const delayFn = (fn: () => void, ms?: number) => {
  const timer = setTimeout(() => {
    fn();
    clearTimeout(timer);
  }, ms || 0);
};

export const shouldHorizontalScroll = (args: {
  scrollLeft: number;
  columnLeft: number;
  containerWidth: number;
  columnWidth: number;
}): boolean => {
  const leftEdge = args.scrollLeft;
  const rightEdge = args.scrollLeft + args.containerWidth;
  const columnRight = args.columnLeft + args.columnWidth;

  const isColumnFullyVisible = args.columnLeft >= leftEdge && columnRight <= rightEdge;

  return !isColumnFullyVisible;
};

/**
 * Insert a row the grid does not know about yet and focus one of its cells,
 * synchronously.
 *
 * For the keyboard handlers that ask the state for a brand-new line (Enter,
 * ArrowDown or ArrowRight past the last row). The state's `rows` prop reaches
 * the grid one effect and one render later; until then the cell the user just
 * left keeps DOM focus, so a keystroke arriving in that gap either edited the
 * finished line or, with the guard that tried to stop that, was dropped — the
 * missing digits of WA-3671 and WA-3803. Going through the grid API instead
 * puts the row into the grid's own store at once, recomputes the
 * virtualisation window (`sortedRowsSet`), and lets the cell take DOM focus
 * in the same synchronous flush as the keydown — before the browser can
 * deliver the next key. The later `rows` prop carries the same row id, so the
 * catch-up is invisible.
 */
export const insertRowAndFocus = (
  api: Pick<
    GridApiCommunity,
    'updateRows' | 'getRowIndexRelativeToVisibleRows' | 'scrollToIndexes' | 'setCellFocus'
  >,
  row: GridValidRowModel & { id: GridRowId },
  field: string,
): void => {
  api.updateRows([row]);
  const rowIndex = api.getRowIndexRelativeToVisibleRows(row.id);
  // The cell focuses itself with `preventScroll`, so reveal the row first.
  if (rowIndex >= 0) api.scrollToIndexes({ rowIndex });
  api.setCellFocus(row.id, field);
};

export const blockArrowKeysInMiddle = (e: React.KeyboardEvent) => {
  if (e.target instanceof HTMLInputElement) {
    const { selectionStart, selectionEnd, value } = e.target;
    if (
      (e.code === 'ArrowLeft' && selectionStart !== 0 && selectionEnd !== 0) ||
      (e.code === 'ArrowRight' && selectionStart !== value.length && selectionEnd !== value.length)
    ) {
      e.stopPropagation();
    }
  }
};

type TChild = ReactElement<any, string | JSXElementConstructor<any>>;

interface IformColumnsAndFooterArgs {
  children:
    | ReactElement<any, string | JSXElementConstructor<any>>
    | ReactElement<any, string | JSXElementConstructor<any>>[];
  columnVisibilityModel: TypeTable['columnVisibilityModel'];
  viewCells: TypeTable['viewCells'];
}

interface IformColumnsAndFooterRes {
  columns: GridColDef[];
  footer: ReactElement | null;
}
